import { Alert, Button, Center, Group, Loader, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Route, Routes } from 'react-router';
import { fetchTenant } from './api/tenant';
import { AccessTokenContext } from './auth/token-context';
import { useAuth } from './auth/use-auth';
import { ForbiddenScreen } from './screens/ForbiddenScreen';
import { LoginScreen } from './screens/LoginScreen';
import { PublicBildirimScreen } from './screens/PublicBildirimScreen';
import { PublicDegerlendirmeScreen } from './screens/PublicDegerlendirmeScreen';
import { AppShellLayout } from './shell/AppShellLayout';
import { KaydedilmemisIcerikKorumasi } from './shell/KaydedilmemisIcerik';
import { ANLAM } from './theme';

/**
 * Kök bileşen (F1·S10 — ilk gerçek arayüz; F3B·S4 public dal).
 *
 * PUBLIC ROTA GRUBU (✅ madde 64 — Kapı 2): `/bildirim` ve (F3B·S7)
 * `/degerlendirme` OTURUM KATMANININ
 * TAMAMEN DIŞINDADIR — `useAuth` yalnız `AuthedApp` içinde çağrılır, public
 * dalda kimlik/oturum kodu HİÇ MOUNT EDİLMEZ (kimlik sızmazlığı rota
 * düzeyinde; e2e kilitler). Ayrı uygulama DEĞİL — teknik karar: tek sayfa +
 * takip sorgusu için ayrı deploy birimi operasyon yükü olurdu.
 */
export function App(): React.JSX.Element {
  return (
    // Madde 221 — kaydedilmemiş içerikten çıkış koruması YÖNLENDİRİCİDE TEK
    // noktada (veri yönlendiricisi içinde; ekranlar yalnız sayı bildirir).
    <KaydedilmemisIcerikKorumasi>
      {/* DEMO ŞERİDİ BURADA DEĞİL (✏️ 25.09.2026 — ürün sahibi): 24.09'da bu
          noktadan dört yüzeye birden çiziliyordu; şerit artık yalnız oturumsuz
          QR sayfalarının kökünde (`shell/DemoSeridi.tsx`). Giriş ekranı da demo
          yüzeyi değildir (demo giriş paketi — uyarı Keycloak formunun
          başlığında). Oturumlu dal (`AuthedApp`: ana kabuk · portal · KVKK
          ekranı) şerit taşımaz; kabuğu kaydıran sarmalayıcı sınıf da kalktı. */}
      <Routes>
        <Route path="/bildirim" element={<PublicBildirimScreen />} />
        {/* F3B·S7 — Kapı 2'nin ÜÇÜNCÜ uygulaması: eğitim değerlendirmesi.
            AYNI dal, aynı gerekçe (kimlik/oturum kodu hiç mount edilmez). */}
        <Route path="/degerlendirme" element={<PublicDegerlendirmeScreen />} />
        <Route path="*" element={<AuthedApp />} />
      </Routes>
    </KaydedilmemisIcerikKorumasi>
  );
}

/**
 * Oturumlu uygulama (F1·S10). Akış:
 *
 *   signed_out → LoginScreen (Keycloak'a yönlendirir; F0·S3 akışı)
 *   signed_in  → /api/tenant sonucuna göre:
 *     forbidden → ForbiddenScreen (403 bir kabul çıktısıdır: pasif /
 *                 silinmiş / kuralsız kimlik ayırt edici mesajla durur)
 *     ok        → AppShellLayout (kabuk + salt-okunur ekranlar)
 *
 * Tenant burada SEÇİLMEZ: sunucu kimliği kendi DB eşlemesinden çözer;
 * ekran yalnız sonucu gösterir.
 */
function AuthedApp(): React.JSX.Element {
  const { state, oturumHatasi, login, logout } = useAuth();

  if (state.kind === 'loading') {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (state.kind === 'error') {
    return (
      <Center h="100vh">
        <Alert color={ANLAM.engel} title="Kimlik hatası" role="alert">
          {state.message}
        </Alert>
      </Center>
    );
  }
  if (state.kind === 'signed_out') {
    return <LoginScreen onLogin={login} />;
  }
  return (
    <AccessTokenContext.Provider value={state.user.access_token}>
      {oturumHatasi && <OturumUyarisi onLogin={login} />}
      <AuthedGate logout={logout} accessToken={state.user.access_token} />
    </AccessTokenContext.Provider>
  );
}

/**
 * OTURUM UYARISI (işlevsel düzeltme turu — B): sessiz yenileme başarısız
 * olduğunda ya da oturum IdP'de kapandığında çıkar.
 *
 * ⚠️ **KABUĞUN YERİNE GEÇMEZ, ÜSTÜNE OTURUR.** Giriş ekranına otomatik
 * yönlendirmek, kullanıcının yarım doldurduğu formu **ekrandan silerdi** —
 * sahadaki kullanıcı formu yarım doldurur, telefona bakar, kaydete basar;
 * verisini kaybederse o formu bir daha doldurmaz. Yeniden giriş kararı bu
 * yüzden KULLANICININDIR ve altındaki ekran yaşamaya devam eder.
 */
function OturumUyarisi({ onLogin }: { onLogin: () => void }): React.JSX.Element {
  return (
    <Alert
      color={ANLAM.dikkat}
      title="Oturum süresi doldu"
      role="alert"
      data-testid="oturum-uyarisi"
      styles={{ root: { position: 'sticky', top: 0, zIndex: 400, borderRadius: 0 } }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm">
          Kaydetme işlemleri başarısız olabilir. Ekrandaki veriler duruyor — yeniden giriş yaptıktan
          sonra kaydedebilirsiniz.
        </Text>
        <Button size="xs" onClick={onLogin} data-testid="oturum-uyarisi-giris">
          Yeniden giriş yap
        </Button>
      </Group>
    </Alert>
  );
}

function AuthedGate({
  logout,
  accessToken,
}: {
  logout: () => void;
  accessToken: string;
}): React.JSX.Element {
  // fetchTenant 403'ü hata değil ayrı kol olarak döndürür (F0·S4 deseni) —
  // TanStack Query'de de "başarılı" sonuçtur, retry döngüsüne girmez.
  const tenant = useQuery({
    queryKey: ['tenant-gate'],
    queryFn: ({ signal }) => fetchTenant(accessToken, signal),
  });

  if (tenant.isPending) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (tenant.isError) {
    return (
      <Center h="100vh">
        <Alert color={ANLAM.engel} title="/api/tenant başarısız" role="alert">
          {tenant.error instanceof Error ? tenant.error.message : 'Bilinmeyen hata'}
        </Alert>
      </Center>
    );
  }
  if (tenant.data.kind === 'forbidden') {
    return <ForbiddenScreen message={tenant.data.message} onLogout={logout} />;
  }
  return <AppShellLayout tenant={tenant.data.tenant} onLogout={logout} />;
}
